<template>
	<div class="single-deal">
		
		<!--------------------------------------------------
			Deal Information
		----------------------------------------------------->
		<div class="deal-top">
			<div class="img">
				<img :src="image" :alt="name" v-if="deal">
				<div class="loader" v-if="!deal">
					<LoadingWheel />
				</div>
			</div>
			<div class="content" v-if="deal">

				<h1 v-if="deal">{{ name }}</h1>
				<h1 v-else><LoadingWheel /> Loading deal</h1>

				<div class="meta" v-if="deal">
					<div><strong>SKU:</strong> {{ sku }}</div>
					<div class="caps"><strong>Store:</strong> {{ store }}</div>
					<div class="caps" v-if="brand.length > 0"><strong>Brand:</strong> {{ brand }}</div>
				</div>

				<div class="buy-wrap" v-if="deal">
					<div class="sale-flag" v-if="onSale">
						<span class="badge badge-green">on sale</span>
					</div>
					<div class="buy">
						<a :href="affiliateUrl" target="_blank">
							<span class="price">
								<span v-if="onSale" class="original-price">{{ retailPrice }}</span>
								{{ price }}
								<i class="fas fa-caret-right"></i>
							</span>
							<span class="lnk" v-if="deal">
								<i class="fas fa-shopping-cart"></i> &nbsp;
								Go To Deal
							</span>
						</a>
					</div>
				</div>

				<div class="stock-notice">
					<div v-if="inStock">
						<span class="badge badge-green mr-3">in stock</span>
						Our systems indicate this deal is in stock. <a :href="affiliateUrl" target="_blank">Visit the deal</a> to see the latest stock updates.
					</div>
					<div v-else>
						<span class="badge badge-red mr-3">out of stock</span>
						Our systems indicate this deal is no longer in stock. <a :href="affiliateUrl" target="_blank">Visit the deal</a> to see the latest stock updates.
					</div>
				</div>

				<div class="summary" v-if="deal">
					<h2>Description</h2>
					<div v-html="description"></div>
				</div>

				<div class="tags" v-if="tags.length > 0">
					<h2>Tags</h2>
					<router-link :to="{ name: 'Tag', params: { tag: t } }" class="badge badge-gray" v-for="(t,i) in tags" :key="[i,t].join('')">{{ t }}</router-link>
				</div>
			</div>
		</div>

		<!--------------------------------------------------
			Related Deals
		----------------------------------------------------->
		<div class="related" v-if="deal">
			<DealsSlider 
				title="Related Deals"
				:params="{
					related_to: dealId,
					limit: 15
				}"
				:showViewAll="false"
			/>
			<DealsSlider 
				:title="['More from', store].join(' ')"
				:params="{
					store: store,
					limit: 15
				}"
				:viewAllUrl="{ name: 'Store', params: { store: store } }"
			/>

			<DealsSlider 
				v-if="((brand !== store) && (brand.length > 0))"
				:title="['More from', brand].join(' ')"
				:params="{
					brand: brand,
					limit: 15
				}"
				:viewAllUrl="{ name: 'Brand', params: { brand: brand } }"
			/>
		</div>

	</div>
</template>

<script>
import LoadingWheel from '@/components/LoadingWheel';
import DealsSlider from '@/components/DealsSlider';
const axios = require('axios');

//
export default {
    created() {

    	this.isLoading = true;
    	this.deal = false;

    	//load the next deal
        this.$store.dispatch('getDeal', this.$route.params.dealId)
        	.then(deal => {
        		this.deal = deal;
        		this.isLoading = false;
        	})
        	.catch(e => {});

    },
	components: {
		LoadingWheel,
		DealsSlider
	},
	data: function() {
		return {
			isLoading: true,
			deal: false
		}
	},
	computed: {
		dealId() {
			return this.deal.deal_id;
		},
		name() {
			try {
				return this.deal.name;
			}catch(e) { return "----"; }
		},
		image() {
			try {
			
				//if we have an original size, use it
				if( this.deal.images.original && this.deal.images.original.length > 0 )
					return this.deal.images.original;

				//if we have a medium size, use it
				if( this.deal.images.medium && this.deal.images.medium.length > 0 )
					return this.deal.images.medium;

				//use the thumb
				return this.deal.images.thumb;

			}catch(e) {
				return "";
			}
		},
		description() {
			try {
				if( this.deal.description.trim().length < 1 ) throw "No descrition";
				return this.deal.description.trim();
			}catch(e) {
				return "No description available for this product."
			}
		},
		sku() {
			try {
				return this.deal.sku;
			}catch(e) { return "n/a"; }
		},
		store() {
			try {
				return this.deal.attributes.store;
			}catch(e) { return "n/a"; }
		},
		brand() {
			try {
				return ( this.deal.attributes.brand ) ? this.deal.attributes.brand : "";
			}catch(e) { return ""; }
		},
		tag() {
			try {
				return this.deal.tags[0];
			}catch(e) {
				return "firearms";
			}
		},
		tags() {
			try {
				return (typeof this.deal.tags === 'object') ? this.deal.tags : [];
			}catch(e) { return []; }
		},
		price() {
			try {

				const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', });

				//if on sale
				if( this.onSale )
					return this.salePrice;

				//return regular price
				return formatter.format(this.deal.prices.price);

			}catch(e) {
				return "n/a";
			}
		},
		salePrice() {
			try {
				const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', });
				return formatter.format(this.deal.prices.sale);
			}catch(e) {
				return "n/a";
			}
		},
		retailPrice() {
			try {
				const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', });
				return formatter.format(this.deal.prices.price);
			}catch(e) {
				return "n/a";
			}
		},
		affiliateUrl() {
			try {
				return this.deal.urls.affiliate;
			}catch(e) {
				return "#";
			}
		},
		onSale() {
			try {
				return ( this.deal.prices.sale && this.deal.prices.sale < this.deal.prices.price ) ? true : false;
			}catch(e) {
				return false;
			}
		},
		inStock() {
			try {
				return ( this.deal.stock.status == 'in-stock' );
			}catch(e) {
				return false;
			}
		}
	},
	methods: {}
};
</script>

<style lang="scss">
.single-deal {
	position: relative;

	//
	.loader {
		height: 400px;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	//
	.deal-top {
		position: relative;
		justify-content: space-between;
		margin-bottom: 100px;
		h1,h2 {
			margin-bottom: 10px;
		}
		.img {
			position: relative;
			display: flex;
			justify-content: center;
			align-items: center;
			border: #ccc 1px solid;
			width: 400px;
			height: 400px;
			flex-shrink: 0;
			max-width: 100%;
			margin-bottom: 30px;
			img {
				display: block;
				margin: 0 auto;
				max-width: 100%;
				max-height: 100%;
				height: auto;
			}
		}
		.content {
			position: relative;
			flex-grow: 1;
		}
		.meta {
			position: relative;
			display: flex;
			margin-bottom: 35px;
			>div {
				margin-left: 30px;
				&:first-child {
					margin-left: 0;
				}
			}
		}
		.summary {
			margin-bottom: 35px;
			line-height: 1.7em;
		}
		.tags {
			.badge {
				margin-right: 6px;
				text-transform: lowercase;
			}
		}
	}
	.sale-flag {
		margin-bottom: 10px;
	}

	//
	.buy {
		display: flex;
		margin-bottom: 35px;

		>* {
			position: relative;
			display: flex;
			justify-content: space-between;
			height: 38px;
			background: #d41b00;
			line-height: 39px;
			color: #fff;
			text-decoration: none;
			overflow: hidden;
		}

		//
		.price {
			position: relative;
			min-width: 120px;
			padding: 0 16px;
			color: #fff;
			font-weight: 500;
			font-size: 23px;

			.original-price {
				margin-right: 5px;
				text-decoration: line-through;
				color: #ffa28d;
			}

			.fa-caret-right {
				position: absolute;
				right: -21px;
				top: 50%;
				margin-top: -32px;
				color: #d41b00;
				font-size: 64px;
			}
		}
		.lnk {
			flex-grow: 1;
			min-width: 150px;
			padding: 0 16px 0 30px;
			line-height: 38px;
			background: #cdc7a2;
			color: #3b3f47;
			text-align: center;
		}

		//
		@media (hover) {
			>* {
				&:hover {
					.lnk {
						background-color: #d2c15c;
					}
				}
			}
		}
	}

	.stock-notice {
		position: relative;
		margin-bottom: 50px;
		a {
			color: #3b3f47;
		}
	}

	//
	@media ( min-width: 900px ) {
		.deal-top {
			display: flex;
		}
		.img {
			margin-bottom: 0;
		}
		.content {
			padding-left: 50px;
		}
	}
}
</style>